/**
 * The shell's live data, each from its real route through @inferrex/api-client:
 *   workspaces        GET /v1/workspaces                 (useMyWorkspaces)
 *   environment       GET /v1/workspace/environment      (useActiveEnvironment — the server's answer)
 *   environments      GET /v1/workspace/environments     (useEnvironmentsList)
 *   capacity          GET /v1/workspace/usage            (useWorkspaceUsage)
 *   notifications     GET /v1/notifications, /unread-count, POST …/:id/read, /read-all
 *
 * Mapping functions are pure and exported for the tests.
 */
import { useQuery } from '@tanstack/react-query';
import {
  getClient, livePollIntervalMs, type ActiveEnvironment, type MyWorkspace, type WorkspaceUsage,
} from '@inferrex/api-client';
import { humanizeStatus, type HeaderCapacityDimension, type ShellEnvironment, type ShellNotification, type ShellWorkspace } from '@inferrex/ui';
import { notificationHref } from '../lib/notification-links';

/**
 * Systems and Relationships against the plan limit. "Systems" is the usage route's
 * `integrations` (the connections count; the field name predates the word). Relationships are
 * distinct connection PAIRS joined by a pipeline in service, never a pipeline count — the server
 * counts them that way and the meter shows its number as-is.
 */
export function capacityDimensions(usage: WorkspaceUsage): HeaderCapacityDimension[] {
  return [
    { label: 'Systems', dimension: 'integrations', used: usage.integrations, limit: usage.integrations_limit },
    { label: 'Relationships', dimension: 'relationships', used: usage.relationships, limit: usage.relationships_limit },
  ];
}

export function toShellWorkspace(w: MyWorkspace): ShellWorkspace {
  return { id: w.id, name: w.name, detail: w.tier ? `${humanizeStatus(w.tier)} plan` : humanizeStatus(w.role) };
}

export function toShellEnvironment(e: { id: string; name: string; class: string | null }): ShellEnvironment {
  return { id: e.id, name: e.name, class: e.class };
}

export function activeShellEnvironment(active: ActiveEnvironment | undefined): ShellEnvironment | null {
  if (!active?.id || !active.name) return null;
  return { id: active.id, name: active.name, class: active.class };
}

export interface NotificationRow {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly resourceType: string | null;
  readonly resourceId: string | null;
  readonly readAt: string | null;
  readonly createdAt: string;
}

export function toShellNotification(n: NotificationRow): ShellNotification {
  return {
    id: n.id,
    title: n.title,
    body: n.body,
    createdAt: n.createdAt,
    read: n.readAt !== null,
    href: notificationHref(n.resourceType, n.resourceId),
  };
}

/** Unread count for the bell. Polls at the workspace's live interval, as the bell does today. */
export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => getClient().notifications.getUnreadNotificationCount(),
    refetchInterval: () => livePollIntervalMs(),
  });
}

/** The 20 newest notifications, fetched while the panel is open. */
export function useRecentNotifications(enabled: boolean) {
  return useQuery({
    queryKey: ['notifications', { pageSize: 20 }],
    queryFn: () => getClient().notifications.listNotifications({ pageSize: 20 }),
    enabled,
    select: (r) => r.notifications.map(toShellNotification),
  });
}
